import React from 'react';

/**
 * EmptyStateIllustrations — Phase 5.5
 *
 * Lightweight token-driven SVGs for empty states. Pass any of these as the
 * `illustration` prop on <EmptyState> to replace the default dashed-icon ring
 * with a hero image.
 *
 * Each illustration:
 *   - Uses CSS tokens (`var(--primary-50)` etc.) so dark mode adapts.
 *   - Inline SVG, no external assets. Bundle cost is negligible.
 *   - Caller controls size via the `size` prop (default 160).
 *   - All decorative — sets aria-hidden so screen readers skip past.
 */

const SIZE = 160;

function svgProps(size = SIZE) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 160 160',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
    role: 'presentation',
  };
}

/** Empty calendar / lessons */
export function NoLessonsIllustration({ size = SIZE } = {}) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="80" cy="80" r="74" fill="var(--primary-50)" />
      {/* calendar body */}
      <rect x="38" y="48" width="84" height="74" rx="10"
        fill="var(--surface-raised)" stroke="var(--primary-300)" strokeWidth="2" />
      {/* header strip */}
      <path d="M38 58 Q38 48 48 48 H112 Q122 48 122 58 V68 H38 Z"
        fill="var(--primary-dark)" />
      {/* binding rings */}
      <rect x="54" y="40" width="6" height="20" rx="3" fill="var(--primary-700)" />
      <rect x="100" y="40" width="6" height="20" rx="3" fill="var(--primary-700)" />
      {/* date grid (rows) */}
      <g stroke="var(--border-light)" strokeWidth="1">
        <line x1="46" y1="84" x2="114" y2="84" />
        <line x1="46" y1="100" x2="114" y2="100" />
        <line x1="46" y1="116" x2="114" y2="116" />
      </g>
      {/* a single highlighted day */}
      <circle cx="80" cy="92" r="6" fill="var(--primary-dark)" />
      <circle cx="80" cy="92" r="6" fill="var(--primary-dark)" opacity="0.25">
        <animate attributeName="r" values="6;14;6" dur="2.6s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.35;0;0.35" dur="2.6s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
}

/** Empty student/clients list */
export function NoStudentsIllustration({ size = SIZE } = {}) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="80" cy="80" r="74" fill="var(--primary-50)" />
      {/* card */}
      <rect x="32" y="46" width="96" height="78" rx="12"
        fill="var(--surface-raised)" stroke="var(--primary-300)" strokeWidth="2" />
      {/* avatar */}
      <circle cx="56" cy="78" r="14" fill="var(--primary-100)" />
      <path d="M56 80 q9 0 14 8 v6 H42 v-6 q5 -8 14 -8 z" fill="var(--primary-100)" />
      {/* name + meta lines */}
      <rect x="78" y="70" width="42" height="6" rx="3" fill="var(--primary-200)" />
      <rect x="78" y="82" width="32" height="4" rx="2" fill="var(--border-light)" />
      <rect x="78" y="92" width="38" height="4" rx="2" fill="var(--border-light)" />
      {/* plus badge */}
      <circle cx="120" cy="50" r="14" fill="var(--success-500)" />
      <path d="M120 44 V56 M114 50 H126" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  );
}

/** Empty search results */
export function NoSearchResultsIllustration({ size = SIZE } = {}) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="80" cy="80" r="74" fill="var(--bg-tertiary)" />
      {/* magnifier glass */}
      <circle cx="68" cy="68" r="26" fill="var(--surface-raised)" stroke="var(--primary-dark)" strokeWidth="4" />
      {/* handle */}
      <rect x="92" y="92" width="26" height="8" rx="4" transform="rotate(45 92 92)"
        fill="var(--primary-dark)" />
      {/* "no match" mark inside */}
      <line x1="58" y1="58" x2="78" y2="78" stroke="var(--text-tertiary)" strokeWidth="3" strokeLinecap="round" />
      <line x1="78" y1="58" x2="58" y2="78" stroke="var(--text-tertiary)" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

/** Offline / no connection */
export function OfflineIllustration({ size = SIZE } = {}) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="80" cy="80" r="74" fill="var(--warning-50)" />
      {/* cloud body */}
      <path d="M50 92 Q34 92 34 76 Q34 60 50 60 Q56 44 76 44 Q98 44 102 64 Q120 66 120 82 Q120 96 104 96 H54 Q50 96 50 92 Z"
        fill="var(--surface-raised)" stroke="var(--warning-600)" strokeWidth="2.5" />
      {/* slash through cloud */}
      <line x1="40" y1="38" x2="118" y2="116" stroke="var(--danger-600)" strokeWidth="5" strokeLinecap="round" />
    </svg>
  );
}

/** Error state */
export function ErrorIllustration({ size = SIZE } = {}) {
  return (
    <svg {...svgProps(size)}>
      <circle cx="80" cy="80" r="74" fill="var(--danger-50)" />
      {/* exclamation triangle */}
      <path d="M80 36 L130 116 H30 Z"
        fill="var(--surface-raised)" stroke="var(--danger-600)" strokeWidth="3" strokeLinejoin="round" />
      <line x1="80" y1="62" x2="80" y2="92" stroke="var(--danger-600)" strokeWidth="6" strokeLinecap="round" />
      <circle cx="80" cy="104" r="4" fill="var(--danger-600)" />
    </svg>
  );
}

export default {
  NoLessons: NoLessonsIllustration,
  NoStudents: NoStudentsIllustration,
  NoSearchResults: NoSearchResultsIllustration,
  Offline: OfflineIllustration,
  Error: ErrorIllustration,
};
